import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ChevronDown, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { trackEvent } from "../lib/track";

const GROUP_ORDER = [["stream", "Stream"], ["rent", "Rent"], ["buy", "Buy"]];
const QUICK = ["US", "GB", "CA", "AU"];

function ProviderRow({ p, titleId }) {
  return (
    <a
      href={p.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        trackEvent("provider_click", { provider: p.name, title_id: titleId });
        api.post("/user/watch-progress", { id: titleId }).catch(() => {});
        toast(`Opening ${p.name} — your spot here is saved.`);
      }}
      className={`group flex items-center gap-2 rounded-[var(--card-radius)] border px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-transform hover:scale-105 ${
        p.subscribed ? "border-[var(--border-accent)]" : "border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      }`}
      style={p.subscribed ? { backgroundColor: p.color, color: p.text } : undefined}
      data-testid={`where-to-watch-link-${p.id}`}
    >
      {p.logo ? (
        <img src={p.logo} alt="" className="svc-logo h-5 w-5 flex-none rounded object-cover" />
      ) : (
        !p.subscribed && <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: p.color }} />
      )}
      <span className="min-w-0 break-words normal-case tracking-normal">Search on {p.name}</span>
      {p.subscribed && <span className="ml-1 flex-none rounded bg-black/25 px-1.5 py-0.5 text-[10px] normal-case tracking-normal">On your plan</span>}
      <ExternalLink size={14} className="flex-none" />
    </a>
  );
}

function RegionGroups({ groups, label, titleId, flatHead }) {
  const nonEmpty = GROUP_ORDER.filter(([k]) => (groups?.[k] || []).length > 0);
  if (!nonEmpty.length) {
    return <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="wtw-region-empty">Not listed for streaming in {label}.</p>;
  }
  return (
    <div className="mt-2 space-y-4">
      {nonEmpty.map(([key, gl]) => (
        <div key={key} data-testid={`wtw-group-${key}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{gl}</p>
          <div className="mt-2 flex flex-wrap gap-2.5">
            {groups[key].map((p) => <ProviderRow key={p.id} p={p} titleId={titleId} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WhereToWatch({ t, user, flatHead }) {
  const wtw = t.where_to_watch || {};
  const groups = wtw.groups || { stream: wtw.stream || [], rent: wtw.rent || [], buy: wtw.buy || [] };
  const homeRegion = wtw.region || "US";
  const homeLabel = wtw.region_label || homeRegion;
  const available = wtw.available_regions || [];
  const homeEmpty = !(groups.stream?.length || groups.rent?.length || groups.buy?.length);
  const noServices = (user?.services || []).length === 0;

  const [moreOpen, setMoreOpen] = useState(false);
  const [activeRegion, setActiveRegion] = useState(null);
  const [cache, setCache] = useState({}); // code -> { status, data }

  const otherRegions = available.filter((r) => r.code !== homeRegion && !QUICK.includes(r.code));
  const quickChips = QUICK.filter((c) => c !== homeRegion);
  const quickLabel = (code) => (available.find((r) => r.code === code)?.label) || code;

  const openRegion = async (code) => {
    if (activeRegion === code) { setActiveRegion(null); return; }
    setActiveRegion(code);
    if (cache[code]) return;
    setCache((c) => ({ ...c, [code]: { status: "loading" } }));
    try {
      const { data } = await api.get(`/catalog/title/${t.id}/watch`, { params: { region: code } });
      setCache((c) => ({ ...c, [code]: { status: "ready", data } }));
    } catch {
      setCache((c) => ({ ...c, [code]: { status: "empty" } }));
    }
  };

  return (
    <div className="mt-10" data-testid="where-to-watch">
      <h2 className={`font-head text-2xl text-[var(--text-primary)] ${flatHead ? "font-bold" : "tracking-wide"}`}>Where to Watch</h2>
      <p className="mt-1 text-xs text-[var(--text-muted)]">We link to each service's search for this title — playback happens on the service, not here.</p>

      {noServices && !homeEmpty && (
        <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="wtw-pick-services">
          Tip: <Link to="/settings" className="font-semibold text-[var(--accent-hover)] underline">pick the services you pay for</Link> in Settings to highlight your options.
        </p>
      )}

      {/* User's region, expanded */}
      <div className="mt-4 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4" data-testid="wtw-home-region">
        <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--text-primary)]">
          <Globe size={15} className="text-[var(--accent)]" /> {homeLabel}
        </p>
        <RegionGroups groups={groups} label={homeLabel} titleId={t.id} flatHead={flatHead} />
      </div>

      {/* More regions */}
      <div className="mt-4" data-testid="wtw-more-regions">
        <button
          type="button"
          onClick={() => setMoreOpen((o) => !o)}
          aria-expanded={moreOpen}
          className="flex w-full items-center justify-between gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-4 py-3 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:border-[var(--accent)]"
          data-testid="wtw-more-regions-toggle"
        >
          <span>More regions</span>
          <ChevronDown size={18} className={`flex-none text-[var(--text-muted)] transition-transform ${moreOpen ? "rotate-180" : ""}`} />
        </button>

        {moreOpen && (
          <div className="mt-3" data-testid="wtw-more-regions-body">
            <div className="flex flex-wrap gap-2" data-testid="wtw-quick-chips">
              {quickChips.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => openRegion(code)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                    activeRegion === code ? "border-[var(--accent)] bg-[var(--accent)]/15 text-[var(--accent-hover)]" : "border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--accent)]"
                  }`}
                  data-testid={`wtw-quick-chip-${code}`}
                >
                  {quickLabel(code)}
                </button>
              ))}
            </div>

            {otherRegions.length > 0 && (
              <div className="mt-3 max-h-64 overflow-y-auto rounded-[var(--card-radius)] border border-[var(--border-subtle)]" data-testid="wtw-region-list">
                {otherRegions.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => openRegion(r.code)}
                    className={`flex w-full items-center justify-between border-b border-[var(--border-subtle)] px-4 py-2.5 text-left text-sm last:border-b-0 transition-colors hover:bg-[var(--accent)]/10 ${
                      activeRegion === r.code ? "text-[var(--accent-hover)]" : "text-[var(--text-primary)]"
                    }`}
                    data-testid={`wtw-region-row-${r.code}`}
                  >
                    <span>{r.label}</span>
                    <ChevronDown size={15} className={`flex-none text-[var(--text-muted)] transition-transform ${activeRegion === r.code ? "rotate-180" : ""}`} />
                  </button>
                ))}
              </div>
            )}

            {activeRegion && (
              <div className="mt-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4" data-testid="wtw-active-region">
                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--text-primary)]">
                  <Globe size={15} className="text-[var(--accent)]" /> {cache[activeRegion]?.data?.region_label || quickLabel(activeRegion)}
                </p>
                {cache[activeRegion]?.status === "loading" && <p className="mt-3 text-sm text-[var(--text-secondary)]">Loading…</p>}
                {cache[activeRegion]?.status === "empty" && <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="wtw-region-empty">Not listed for streaming in {quickLabel(activeRegion)}.</p>}
                {cache[activeRegion]?.status === "ready" && (
                  <RegionGroups groups={cache[activeRegion].data.groups} label={cache[activeRegion].data.region_label} titleId={t.id} flatHead={flatHead} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
